import type { IShopifyPageAssignedResponse } from '~/modules/shopify/types';
import type { TemplateType } from '~/types/template';

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchFromAPI } from '~/api/utils';

const PAGE_LIMIT = 1000;
const MAX_PAGES = 5; // 10 pages × 1000 = 10,000 items max

type PagedResponse = {
  data: IShopifyPageAssignedResponse;
  nextCursor: string | undefined;
  hasNextPage: boolean;
};

const fetchPage = async (type: TemplateType, cursor?: string): Promise<PagedResponse> => {
  return fetchFromAPI(`/api/shopify/assigned-template-paged?type=${type}`, {
    method: 'POST',
    body: JSON.stringify({ type, limit: PAGE_LIMIT, cursor }),
  });
};

const mergeResponse = (
  prev: IShopifyPageAssignedResponse,
  next: IShopifyPageAssignedResponse,
): IShopifyPageAssignedResponse => {
  const merged = { ...prev };
  for (const [suffix, value] of Object.entries(next)) {
    if (merged[suffix]) {
      merged[suffix] = {
        // url from first page is most recent (server sorts by updatedAt desc)
        url: merged[suffix].url,
        total: merged[suffix].total + value.total,
        data: [...merged[suffix].data, ...value.data],
      };
    } else {
      merged[suffix] = value;
    }
  }
  return merged;
};

export const useQueryShopifyAssignedPaged = (
  variables?: { type: TemplateType },
  enabled = true,
) => {
  const [data, setData] = useState<IShopifyPageAssignedResponse>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isFetchingRef = useRef(false);

  const fetchAll = useCallback(async () => {
    if (!variables?.type || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsLoading(true);
    setIsDone(false);
    setError(null);
    setData({});

    try {
      let cursor: string | undefined = undefined;
      let hasNextPage = true;
      let pageCount = 0;

      while (hasNextPage && pageCount < MAX_PAGES) {
        const result = await fetchPage(variables.type, cursor);
        setData((prev) => mergeResponse(prev, result.data));
        hasNextPage = result.hasNextPage;
        cursor = result.nextCursor;
        pageCount++;
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch assigned data'));
    } finally {
      setIsLoading(false);
      setIsDone(true);
      isFetchingRef.current = false;
    }
  }, [variables?.type]);

  useEffect(() => {
    if (enabled) {
      fetchAll();
    }
  }, [enabled, fetchAll]);

  return { data, isLoading, isDone, error, refetch: fetchAll };
};
